import { Box, Collapse, CircularProgress, TableContainer, Table, TableHead, TableRow, TableBody, Tooltip, TableCell, Typography, IconButton, Pagination, Autocomplete, TextField } from "@mui/material"
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/Remove';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useEffect, useState } from "react";
import { handleApiRequest } from "../../hooks/callApi";
import { useFetch } from "../../hooks/useAPI";

/**
 * Gets the correct icon to display for each audit event.
 * @param {String} changeType The string of the audit change type. 
 * @returns React icon to display.
 */
function ChangeTypeIcon(changeType) {
    if (changeType === "created") {
        return (<Tooltip title="Created"><AddIcon sx={{ color: "#78c26d" }} /></Tooltip>)
    }
    else if (changeType === "updated") {
        return (<Tooltip title="Updated"><EditIcon sx={{ color: "#c2a96d" }} /></Tooltip>)
    }
    else if (changeType === "deleted") {
        return (<Tooltip title="Deleted"><RemoveIcon sx={{ color: "#c26d6d" }} /></Tooltip>)
    }

    return (<Tooltip title="Unknown"><QuestionMarkIcon /></Tooltip>)
}

/**
 * Gets the corect category name to display from a laravel model.
 */
function GetAuditDisplayName(auditModel) {
    const cleanedName = auditModel.replace("App\\Models\\", "");
    return cleanedName;
}

/**
 * Checks if the given change type would have new changes given.
 * @param {String} changeType The change type to check.
 */
function HasNewChanges(changeType) {
    return (changeType === "created" || changeType === "updated") ? true : false;
}

/**
 * Checks if the given change type would have old changes given.
 * @param {String} changeType The change type to check. Should be the audit's event field.
 */
function HasOldChanges(changeType) {
    return (changeType === "deleted" || changeType === "updated") ? true : false;
}

/**
 * Gets the name of the given auditable from audit data.
 * @param {*} auditData The audit data to find the auditable's name.
 * @returns String of name, or "No Object" if not found.
 */
function GetAuditableName(auditData) {
    // See if given auditable for this audit has a name field.
    if (auditData.auditable && Object.hasOwn(auditData.auditable, "name")) {
        return auditData.auditable.name;
    }

    // Audit was deleted, try and see if old_values contained a name field.
    if (auditData.event === "deleted" && Object.hasOwn(auditData.old_values, "name")) {
        return auditData.old_values.name;
    }

    if (auditData.event === "created" && Object.hasOwn(auditData.new_values, "name")) {
        return auditData.new_values.name;
    }

    return "No Object";
}

function AuditWarning({ auditableValid }) {
    if (!auditableValid) {
        return (
            <Box display="flex">
                <WarningAmberIcon color="warning" />
                <Typography color="warning">The audited object does not exist anymore. It was likely deleted.</Typography>
            </Box>
        )
    }

    return null;
}

/**
 * audit is an object with the following:
 * id: Unique audit ID that represents this audit.
 * user_type: Laravel user type that performed the event.
 * user_id: The actual user ID in the database.
 * event: The type of event that was performed: created|updated|deleted
 * auditable_type: The model type the audit was performed on.
 * old_values: The old values before the change.
 * new_values: The new values after the change.
 * created_at: When the audit was created.
 */

/**
 * AuditRow is used within the AuditTable to display a singular audit.
 * @param {*} audit The object containing the data for a singular audit.
 * @returns React component.
 */
function AuditRow({ audit }) {
    const [open, setOpen] = useState(false);

    /**
     * Returns a react component that returns table rows of each new and old change for an audit.
     * @returns React component with TableRows.
     */
    function GetAuditChanges() {

        function DisplayValue(value) {
            if (value == null) {
                return "-";
            }

            if ((typeof value) === 'string') {
                return (value.length > 0) ? value : "-";
            }

            // Something other than a string.
            return value;
        }

        if (HasOldChanges(audit.event)) {
            const oldValues = Object.entries(audit.old_values);
            const newValues = Object.entries(audit.new_values);

            return (
                oldValues.map(([key, value], index) => (
                    <TableRow key={key}>
                        <TableCell>{key}</TableCell>
                        <TableCell>{Object.hasOwn(audit.new_values, key) ? newValues[index][1] : "-"}</TableCell>
                        <TableCell>{DisplayValue(value)}</TableCell>
                    </TableRow>
                ))
            )
        }

        const oldValues = Object.entries(audit.old_values);
        const newValues = Object.entries(audit.new_values);
        return (
            newValues.map(([key, value], index) => (
                <TableRow key={key}>
                    <TableCell>{key}</TableCell>
                    <TableCell>{DisplayValue(value)}</TableCell>
                    <TableCell>{Object.hasOwn(audit.old_values, key) ? oldValues[index][1] : "-"}</TableCell>
                </TableRow>
            ))
        )
    }

    return (
        <>
            <TableRow>
                <TableCell> {ChangeTypeIcon(audit.event)} </TableCell>
                <TableCell> {audit.user.first_name + " " + audit.user.last_name} </TableCell>
                <TableCell> {GetAuditDisplayName(audit.auditable_type)} </TableCell>
                <TableCell> {GetAuditableName(audit)} </TableCell>
                <TableCell> {new Date(audit.created_at).toLocaleString()} </TableCell>
                <TableCell>
                    <Tooltip title="View Changes">
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="h5">
                                Changes
                            </Typography>
                            <Typography variant="subtitle1" gutterBotton>
                                Audit Performed On:
                                {" " + GetAuditableName(audit)}
                            </Typography>
                            <AuditWarning auditableValid={audit.auditable != null} />
                            <Table size="small" sx={{ width: "50%" }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Field</TableCell>
                                        <TableCell>New Value</TableCell>
                                        <TableCell>Old Value</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {GetAuditChanges()}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

/**
 * Current audited objects are:
 * Project
 * ProjectTask
 * Portfolio
 * Department
 */

const modelFilterOptions = [
    { label: 'Project', id: "Project" },
    { label: 'Project Task', id: "ProjectTask" },
    { label: 'Portfolio', id: "Portfolio" },
    { label: 'Department', id: "Department" },
];

/**
 * A react component that displays the entire table for auditing.
 */
function AuditTable() {

    const [auditData, setAuditData] = useState([]); // The actual audit data for the current page.
    const [isLoading, setIsLoading] = useState(true); // True if waiting on response.

    const [currentPage, setCurrentPage] = useState(1); // Current page number.
    const [lastPage, setLastPage] = useState(1); // Last page number.

    const [filterPersonID, setFilterPersonID] = useState(''); // ID of user to filter by.

    const [filterModelName, setFilterModelName] = useState(''); // Model name to filter by.

    const [filterStartDate, setFilterStartDate] = useState(''); // Start date to filter. Will give audits on and after this date.
    const [filterEndDate, setFilterEndDate] = useState(''); // End date to filter. Will give audits on or before this date.

    // Drop down response for filtering by person.
    const { data: peopleDropdownRes } = useFetch(`/api/people-dropdown-email?extra_params=0&dept=0`);

    const handlePersonChange = (_event, newPerson) => {
        setFilterPersonID(newPerson?.id || '');
        // This will cause a useEffect call.
    };

    const handleModelChange = (_event, newModel) => {
        setFilterModelName(newModel?.id || '');
        // This will cause a useEffect call.
    };

    useEffect(() => {
        setIsLoading(true);

        /**
         * Async function to get audit data.
         */
        async function getAuditData() {
            const response = await handleApiRequest(`/api/get-audits?userFilterID=${filterPersonID}&category=${filterModelName}&startDate=${filterStartDate}&endDate=${filterEndDate}&page=${currentPage}`, "get");
            setAuditData(response.data.data.data);

            const respCurPage = response.data.data.current_page;
            const respLastPage = response.data.data.last_page;

            // This causes a double request and render.
            setCurrentPage(Math.min(respCurPage, respLastPage));
            setLastPage(respLastPage);
            setIsLoading(false);
        }

        getAuditData();
    }, [currentPage, filterPersonID, filterModelName, filterStartDate, filterEndDate]);

    return (
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
            <Typography variant="subtitle1" margin={1}>Filter By:</Typography>
            <Box display={"flex"}>
                <Box sx={{ width: '25%', margin: 1 }}>
                    <Autocomplete
                        disablePortal
                        options={!peopleDropdownRes ? [{ label: "Loading...", id: 0 }] : peopleDropdownRes.data.peopleDropdown}
                        getOptionLabel={(option) =>
                            `${option.first_name} ${option.last_name} [${option.email}]`
                        }
                        onChange={handlePersonChange}
                        renderInput={(params) => <TextField {...params} label="User" />}
                    />
                </Box>

                <Box sx={{ width: '25%', margin: 1 }}>
                    <Autocomplete
                        disablePortal
                        options={modelFilterOptions}
                        onChange={handleModelChange}
                        renderInput={(params) => <TextField {...params} label="Category" />}
                    />
                </Box>

                <Box sx={{ width: '25%', margin: 1 }}>
                    <DatePicker
                        label="Start Date"
                        slotProps={{
                            field: {clearable: true}
                        }}
                        onChange={(date) => setFilterStartDate(date ? date.format('YYYY-MM-DD') : '')}
                    />
                </Box>

                <Box sx={{ width: '25%', margin: 1 }}>
                    <DatePicker
                        label="End Date"
                        slotProps={{
                            field: {clearable: true}
                        }}
                        onChange={(date) => setFilterEndDate(date ? date.format('YYYY-MM-DD') : '')}
                    />
                </Box>
            </Box>

            {isLoading ? (
                <Box textAlign="center" mt={4}>
                    {/* Show loading circle until async call is finished. */}
                    <CircularProgress />
                </Box>
            ) : (<TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Change Type</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Audit Object</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {auditData.map((singleAudit) => (
                            <AuditRow audit={singleAudit} key={singleAudit.id} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>)}

            <Box display="flex" justifyContent="center" marginTop={2} marginBottom={2}>
                <Pagination
                    count={lastPage}
                    page={currentPage}
                    onChange={(_event, v) => setCurrentPage(v)}
                    color="primary"
                />
            </Box>
        </Box>
    )
}

export default AuditTable